import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { useAuthFetch } from '../hooks/useAuthFetch';
import '../stili/BachecaRisultati.css';

const API_URL = process.env.REACT_APP_API_URL;

export default function BachecaRisultati() {
  const [eventi, setEventi] = useState([]);
  const [idEvento, setIdEvento] = useState(null);
  const [nomeEvento, setNomeEvento] = useState('');
  const [risultati, setRisultati] = useState([]);
  const [mostraInBacheca, setMostraInBacheca] = useState(false);

  const { user } = useAuth();
  const navigate = useNavigate();
  const authFetch = useAuthFetch();

  useEffect(() => {
    if (!user?.accessToken) navigate('/login');
  }, [user, navigate]);

  const fetchEventi = useCallback(async () => {
    try {
      const res = await authFetch(`${API_URL}/api/admin/eventogara`);
      if (!res.ok) throw new Error('Errore caricamento eventi');
      const data = await res.json();
      setEventi(data);
    } catch (error) {
      alert('Errore nel caricamento eventi: ' + error.message);
    }
  }, [authFetch]);

  useEffect(() => {
    fetchEventi();
  }, [fetchEventi]);

  const fetchGare = async (eventoId) => {
    try {
      const res = await authFetch(`${API_URL}/api/admin/gara/evento/${eventoId}`);
      if (!res.ok) throw new Error('Errore caricamento gare');
      const data = await res.json();
      return data;
    } catch (error) {
      alert('Errore nel caricamento gare: ' + error.message);
      return [];
    }
  };

  const fetchRisultati = async (eventoId, gareEvento) => {
    try {
      const res = await authFetch(`${API_URL}/api/admin/risultati-gara/evento/${eventoId}`);
      if (!res.ok) throw new Error('Errore caricamento risultati');
      const data = await res.json();

      const risultatiConSquadra = data.map(r => {
        const gara = gareEvento.find(g => g.idGara === r.idGara);
        return { ...r, squadraAvversaria: gara?.gara || '-', luogoGara: gara?.luogoGara || '-' };
      });

      risultatiConSquadra.sort((a, b) => a.categoria.localeCompare(b.categoria));
      setRisultati(risultatiConSquadra);
    } catch (error) {
      alert('Errore nel caricamento risultati: ' + error.message);
    }
  };

  const handleEventoChange = async (e) => {
    const selectedId = e.target.value;
    setIdEvento(selectedId);
    setMostraInBacheca(false);

    const evento = eventi.find(ev => ev.idEvento.toString() === selectedId);
    setNomeEvento(evento?.nomeEvento || '');

    const gareEvento = await fetchGare(selectedId);
    fetchRisultati(selectedId, gareEvento);
  };

  const handleMostraBacheca = async () => {
    if (!idEvento) { alert('Seleziona un evento prima'); return; }
    try {
      const res = await authFetch(`${API_URL}/api/admin/risultati-gara/bacheca/${idEvento}`, { method: 'PUT' });
      if (!res.ok) throw new Error('Errore durante la pubblicazione');
      setMostraInBacheca(true);
      alert('Risultati pubblicati nella bacheca!');
    } catch (error) {
      alert('Errore: ' + error.message);
    }
  };

  const formatMarcatori = (reti) => {
    if (!reti || reti.length === 0) return '-';
    const conteggio = reti.reduce((acc, r) => {
      if (r.marcatore) acc[r.marcatore] = (acc[r.marcatore] || 0) + 1;
      return acc;
    }, {});
    return Object.entries(conteggio).map(([name, tot], idx) => <div key={idx}>{name} ({tot})</div>);
  };

  const formatAssistman = (reti) => {
    if (!reti || reti.length === 0) return '-';
    const conteggio = reti.reduce((acc, r) => {
      if (r.assistMan) acc[r.assistMan] = (acc[r.assistMan] || 0) + 1;
      return acc;
    }, {});
    return Object.entries(conteggio).map(([name, tot], idx) => <div key={idx}>{name} ({tot})</div>);
  };

  /* ------------------------------------
   RESET RISULTATI BACHECA
   (Setta visibileBacheca = 0 su tutti)
-------------------------------------- */
const resetBachecaRisultati = async () => {
  try {
    const resp = await authFetch(
      `${API_URL}/api/admin/risultati-gara/bacheca/reset`,
      { method: "PUT" }
    );

    if (!resp.ok) {
      console.error("Errore reset bacheca risultati:", resp.status);
      return;
    }

    console.log("Reset bacheca risultati effettuato");

    // Ricarica lista eventi
    await fetchEventi();

    // Reset stati locali
    setMostraInBacheca(false);
    setIdEvento(null);
    setNomeEvento("");
    setRisultati([]);

  } catch (err) {
    console.error("Errore:", err);
  }
};


  return (
  <div className="bacheca-risultati-container" translate="no">
    <h2>Bacheca Risultati</h2>

    <select
      className="bacheca-risultati-select"
      value={idEvento || ''}
      onChange={handleEventoChange}
    >
      <option value="">Seleziona evento</option>
      {eventi.map(ev => (
        <option key={ev.idEvento} value={ev.idEvento}>
          {ev.nomeEvento}
        </option>
      ))}
    </select>

    {risultati.length > 0 && (
      <>
        <h3>Risultati per evento: {nomeEvento}</h3>

        <table className="bacheca-risultati-tabella">
          <thead>
            <tr>
              <th>Categoria</th>
              <th>Gara</th>
              <th>Risultato</th>
              <th>Marcatori</th>
              <th>Assistman</th>
              <th>Osservazioni</th>
            </tr>
          </thead>
          <tbody>
            {risultati.map((r, i) => (
              <tr
                key={i}
                className={
                  i % 2 === 0
                    ? 'bacheca-risultati-riga-dispari'
                    : 'bacheca-risultati-riga-pari'
                }
              >
                <td data-label="Categoria">{r.categoria}</td>
                <td data-label="Gara">
                  {r.luogoGara === 'IN CASA' ? (
                    <>
                      <strong>MONTEROTONDO 1935</strong> vs {r.squadraAvversaria}
                    </>
                  ) : (
                    <>
                      {r.squadraAvversaria} vs <strong>MONTEROTONDO 1935</strong>
                    </>
                  )}
                </td>
                <td data-label="Risultato">{r.risultato || '-'}</td>
                <td data-label="Marcatori">{formatMarcatori(r.reti)}</td>
                <td data-label="Assistman">{formatAssistman(r.reti)}</td>
                <td data-label="Osservazioni">{r.osservazioniGara || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </>
    )}

    <div className="bacheca-risultati-button-row">
  {idEvento && ( // i bottoni appaiono solo se un evento è selezionato
    <>
      <button
        className="bacheca-risultati-bottone-pubblica"
        onClick={handleMostraBacheca}
      >
        Mostra nella bacheca della home
      </button>

      <button
        className="bacheca-risultati-bottone-pubblica"
        onClick={resetBachecaRisultati}
        style={{ marginLeft: '10px' }}
      >
        Reset
      </button>
    </>
  )}

  {mostraInBacheca && (
    <p className="bacheca-risultati-success-message">
      ✅ I risultati sono ora visibili nella bacheca!
    </p>
  )}
</div>


    <div className="pg-bacheca-risultati-button-row">
            <button
              className="pg-bacheca-risultati-custom-button"
              type="button"
              onClick={() => navigate('/gestionale')}
            >
          Torna al Gestionale
        </button>
      </div>
  </div>
);
}